import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  FileText,
  ChevronDown,
  ChevronUp,
  Filter,
  Search,
  User,
  Calendar,
  Settings,
  ArrowLeftRight,
  Plane,
  Shield,
  ChevronLeft,
  ChevronRight,
  Sparkles,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ENTITY_CONFIG = {
  user: { label: 'User', icon: User, color: 'bg-blue-100 text-blue-700' },
  auth: { label: 'Auth', icon: Shield, color: 'bg-slate-100 text-slate-700' },
  schedule: { label: 'Schedule', icon: Calendar, color: 'bg-green-100 text-green-700' },
  schedule_week: { label: 'Schedule Week', icon: Calendar, color: 'bg-green-100 text-green-700' },
  pto_request: { label: 'PTO Request', icon: Plane, color: 'bg-purple-100 text-purple-700' },
  pto_type_config: { label: 'PTO Type', icon: Settings, color: 'bg-purple-50 text-purple-600' },
  wfh_request: { label: 'WFH Request', icon: FileText, color: 'bg-amber-100 text-amber-700' },
  wfh_type_config: { label: 'WFH Type', icon: Settings, color: 'bg-amber-50 text-amber-600' },
  system_settings: { label: 'Settings', icon: Settings, color: 'bg-gray-100 text-gray-700' },
  swap_request: { label: 'Shift Swap', icon: ArrowLeftRight, color: 'bg-orange-100 text-orange-700' },
  shift_offer: { label: 'Open Shift', icon: ArrowLeftRight, color: 'bg-teal-100 text-teal-700' },
  shift_template: { label: 'Shift Template', icon: Calendar, color: 'bg-teal-100 text-teal-700' },
  shift_type_config: { label: 'Shift Type', icon: Settings, color: 'bg-teal-50 text-teal-600' },
  rotation: { label: 'Rotation', icon: Calendar, color: 'bg-cyan-100 text-cyan-700' },
  team: { label: 'Team', icon: User, color: 'bg-indigo-100 text-indigo-700' },
  company_holiday: { label: 'Holiday', icon: Calendar, color: 'bg-rose-100 text-rose-700' },
  holiday_volunteer: { label: 'Holiday Volunteer', icon: User, color: 'bg-rose-50 text-rose-600' },
  activity: { label: 'Activity', icon: FileText, color: 'bg-lime-100 text-lime-700' },
  activity_type: { label: 'Activity Type', icon: Settings, color: 'bg-lime-50 text-lime-600' },
  feedback: { label: 'Feedback', icon: FileText, color: 'bg-pink-100 text-pink-700' },
  role_permissions: { label: 'Role Permissions', icon: Shield, color: 'bg-violet-100 text-violet-700' },
  lunch_break_policy: { label: 'Lunch Policy', icon: Settings, color: 'bg-yellow-100 text-yellow-700' },
  staffing_rules: { label: 'Staffing Rules', icon: Settings, color: 'bg-sky-100 text-sky-700' },
  employee: { label: 'Employee', icon: User, color: 'bg-blue-50 text-blue-600' },
  flex_bank: { label: 'Flex Bank', icon: Plane, color: 'bg-teal-50 text-teal-600' },
};

const ACTION_CONFIG = {
  create: { label: 'Created', color: 'bg-green-100 text-green-700' },
  update: { label: 'Updated', color: 'bg-blue-100 text-blue-700' },
  delete: { label: 'Deleted', color: 'bg-red-100 text-red-700' },
  approve: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700' },
  deny: { label: 'Denied', color: 'bg-red-100 text-red-700' },
  cancel: { label: 'Cancelled', color: 'bg-gray-100 text-gray-700' },
  toggle_active: { label: 'Toggled Active', color: 'bg-amber-100 text-amber-700' },
  force_password_reset: { label: 'Password Reset', color: 'bg-red-100 text-red-700' },
  admin_reset_password: { label: 'Password Reset', color: 'bg-red-100 text-red-700' },
  login: { label: 'Login', color: 'bg-slate-100 text-slate-600' },
  reset: { label: 'Reset', color: 'bg-amber-100 text-amber-700' },
  revoke: { label: 'Revoked', color: 'bg-red-100 text-red-700' },
  lock: { label: 'Locked', color: 'bg-yellow-100 text-yellow-700' },
  unlock: { label: 'Unlocked', color: 'bg-green-100 text-green-600' },
  accept: { label: 'Accepted', color: 'bg-emerald-100 text-emerald-700' },
  claim: { label: 'Claimed', color: 'bg-teal-100 text-teal-700' },
  apply: { label: 'Applied', color: 'bg-blue-100 text-blue-700' },
  bulk_delete: { label: 'Bulk Deleted', color: 'bg-red-100 text-red-700' },
  bulk_delete_filtered: { label: 'Bulk Deleted', color: 'bg-red-100 text-red-700' },
  bulk_create: { label: 'Bulk Created', color: 'bg-green-100 text-green-700' },
  bulk_business_directed_wfh: { label: 'Bulk WFH', color: 'bg-amber-100 text-amber-700' },
  update_location: { label: 'Location Updated', color: 'bg-blue-100 text-blue-700' },
  update_profile: { label: 'Profile Updated', color: 'bg-blue-100 text-blue-700' },
};

const formatTimestamp = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
  });
};

const ChangesDiff = ({ changes }) => {
  if (!changes || Object.keys(changes).length === 0) return null;
  const formatKey = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return (
    <div className="mt-2 space-y-1">
      {Object.entries(changes).map(([key, { from: fromVal, to: toVal }]) => (
        <div key={key} className="flex items-start text-xs gap-2">
          <span className="font-medium text-gray-600 min-w-[100px]">{formatKey(key)}:</span>
          <span className="text-red-600 line-through">{String(fromVal ?? '-')}</span>
          <span className="text-gray-400">&rarr;</span>
          <span className="text-green-700 font-medium">{String(toVal ?? '-')}</span>
        </div>
      ))}
    </div>
  );
};

const AuditEntry = ({ entry, userMap }) => {
  const [expanded, setExpanded] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const entityCfg = ENTITY_CONFIG[entry.entity_type] || { label: entry.entity_type, icon: FileText, color: 'bg-gray-100 text-gray-700' };
  const actionCfg = ACTION_CONFIG[entry.action] || { label: entry.action, color: 'bg-gray-100 text-gray-700' };
  const EntityIcon = entityCfg.icon;
  const hasChanges = entry.changes && Object.keys(entry.changes).length > 0;
  const hasDetails = hasChanges || entry.before || entry.after || entry.metadata;
  const isAiAction = entry.metadata?.source === 'ai_chat' || (entry.performed_by_name || '').includes('AI Chat');

  // Resolve user name: check metadata first, then look up entity_id in user map
  const resolveName = (id) => userMap[id] || null;
  const targetName = entry.metadata?.target_user_name
    || entry.metadata?.user_name
    || resolveName(entry.metadata?.user_id)
    || resolveName(entry.entity_id)
    || null;

  // Build a readable description of who was affected
  const getTargetDescription = () => {
    const parts = [];
    if (targetName) parts.push(targetName);
    // For swap requests, show both parties
    const reqName = resolveName(entry.metadata?.requesting_user_id);
    const accName = resolveName(entry.metadata?.accepting_user_id);
    if (reqName && accName) {
      return `${reqName} ↔ ${accName}`;
    } else if (reqName) {
      parts.push(reqName);
    }
    return parts[0] || null;
  };

  const displayName = getTargetDescription();

  // Filter and resolve metadata for display - replace IDs with names
  const resolveMetaValue = (key, value) => {
    if (typeof value === 'string' && (key.endsWith('_id') || key === 'user_id')) {
      const name = resolveName(value);
      return name ? `${name}` : value;
    }
    return String(value);
  };

  // Keys to hide from metadata (already shown in header or redundant)
  const hiddenMetaKeys = new Set(['target_user_name', 'user_name', 'source', 'target_user_id', 'user_id', 'requesting_user_id', 'accepting_user_id']);

  // Format a meta key for display
  const formatKey = (key) => {
    return key.replace(/_/g, ' ').replace(/ id$/, '').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Check if a value looks like a UUID
  const isUUID = (v) => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);

  return (
    <div className="border border-gray-100 rounded-lg hover:border-gray-200 transition-colors" data-testid={`audit-entry-${entry.id}`}>
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer"
        onClick={() => hasDetails && setExpanded(!expanded)}
      >
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          <div className={`p-1.5 rounded ${entityCfg.color}`}>
            <EntityIcon className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center space-x-2 flex-wrap">
              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${actionCfg.color}`}>
                {actionCfg.label}
              </span>
              <span className={`text-xs px-1.5 py-0.5 rounded ${entityCfg.color}`}>
                {entityCfg.label}
              </span>
              {isAiAction && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium flex items-center gap-0.5">
                  <Sparkles className="h-2.5 w-2.5" /> AI
                </span>
              )}
              {!entry.summary && displayName && (
                <span className="text-sm font-medium text-gray-900 truncate">{displayName}</span>
              )}
            </div>
            {entry.summary && (
              <p className="text-sm font-medium text-gray-900 mt-1" data-testid={`audit-summary-${entry.id}`}>
                {entry.summary}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-0.5">
              by <span className="font-medium">{entry.performed_by_name || 'System'}</span>
              <span className="mx-1">&middot;</span>
              {formatTimestamp(entry.timestamp)}
            </p>
          </div>
        </div>
        {hasDetails && (
          <div className="ml-2 text-gray-400">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        )}
      </div>
      {expanded && hasDetails && (
        <div className="px-4 pb-3 border-t border-gray-50">
          {hasChanges && <ChangesDiff changes={entry.changes} />}
          {!hasChanges && (entry.before || entry.after) && (
            <div className="mt-2 space-y-1">
              {entry.before && Object.entries(entry.before).filter(([k]) => !k.startsWith('_')).map(([key, val]) => (
                <div key={`before-${key}`} className="flex items-start text-xs gap-2">
                  <span className="font-medium text-gray-600 min-w-[100px]">{formatKey(key)}:</span>
                  <span className="text-red-600 line-through">{isUUID(val) ? (resolveName(val) || val) : String(val ?? '-')}</span>
                </div>
              ))}
              {entry.after && Object.entries(entry.after).filter(([k]) => !k.startsWith('_')).map(([key, val]) => (
                <div key={`after-${key}`} className="flex items-start text-xs gap-2">
                  <span className="font-medium text-gray-600 min-w-[100px]">{formatKey(key)}:</span>
                  <span className="text-green-700 font-medium">{isUUID(val) ? (resolveName(val) || val) : String(val ?? '-')}</span>
                </div>
              ))}
            </div>
          )}
          {entry.metadata && Object.keys(entry.metadata).length > 0 && (
            <div className="mt-2 text-xs text-gray-500">
              {Object.entries(entry.metadata)
                .filter(([k, v]) => v != null && !hiddenMetaKeys.has(k) && !isUUID(v))
                .map(([k, v]) => (
                <span key={k} className="inline-block mr-3">
                  <span className="text-gray-400">{formatKey(k)}:</span> {resolveMetaValue(k, v)}
                </span>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowRaw(!showRaw); }}
            className="mt-2 text-xs text-gray-400 hover:text-gray-700 underline"
            data-testid={`audit-raw-toggle-${entry.id}`}
          >
            {showRaw ? 'Hide raw details' : 'View raw details'}
          </button>
          {showRaw && (
            <pre className="mt-2 p-3 bg-gray-900 text-gray-100 text-[11px] rounded-md overflow-x-auto whitespace-pre-wrap break-all" data-testid={`audit-raw-${entry.id}`}>
              {JSON.stringify(entry, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

const AuditLog = () => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    entity_type: '',
    action: '',
    user_id: '',
    search: '',
    start_date: '',
    end_date: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [entityTypes, setEntityTypes] = useState([]);
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const pageSize = 50;

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const authHeaders = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const [typesRes, usersRes] = await Promise.all([
          axios.get(`${API}/api/audit-log/entity-types`, { headers: authHeaders }),
          axios.get(`${API}/api/employees`, { headers: authHeaders })
        ]);
        setEntityTypes(typesRes.data.entity_types || []);
        setActions(typesRes.data.actions || []);
        setUsers(usersRes.data || []);
      } catch (e) { /* ignore */ }
    };
    loadMeta();
  }, []);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: pageSize, skip: page * pageSize };
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.action) params.action = filters.action;
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.search) params.search = filters.search;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      const res = await axios.get(`${API}/api/audit-log`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Failed to fetch audit log:', e);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // Debounce the free-text search into the filters
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters(prev => (prev.search === searchInput ? prev : { ...prev, search: searchInput }));
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Build user ID -> name lookup map
  const userMap = React.useMemo(() => {
    const map = {};
    users.forEach(u => { map[u.id] = u.name || u.username; });
    return map;
  }, [users]);

  const totalPages = Math.ceil(total / pageSize);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({ entity_type: '', action: '', user_id: '', search: '', start_date: '', end_date: '' });
    setSearchInput('');
    setPage(0);
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4" data-testid="audit-log-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-slate-100">
            <Shield className="h-5 w-5 text-slate-700" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Audit Log</h1>
            <p className="text-sm text-gray-500">{total} total entries</p>
          </div>
        </div>
        <Button
          variant={showFilters ? "default" : "outline"}
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          data-testid="toggle-filters-btn"
        >
          <Filter className="h-4 w-4 mr-1" />
          Filters {hasActiveFilters && `(${Object.values(filters).filter(v => v).length})`}
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by person, action, type, or details..."
          className="w-full text-sm border border-gray-200 rounded-md pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-300"
          data-testid="audit-search-input"
        />
      </div>

      {showFilters && (
        <Card data-testid="audit-filters">
          <CardContent className="pt-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Type</label>
                <select
                  className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5"
                  value={filters.entity_type}
                  onChange={(e) => handleFilterChange('entity_type', e.target.value)}
                  data-testid="filter-entity-type"
                >
                  <option value="">All Types</option>
                  {entityTypes.map(t => (
                    <option key={t} value={t}>{(ENTITY_CONFIG[t]?.label || t)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Action</label>
                <select
                  className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5"
                  value={filters.action}
                  onChange={(e) => handleFilterChange('action', e.target.value)}
                  data-testid="filter-action"
                >
                  <option value="">All Actions</option>
                  {actions.map(a => (
                    <option key={a} value={a}>{(ACTION_CONFIG[a]?.label || a)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">User</label>
                <select
                  className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5"
                  value={filters.user_id}
                  onChange={(e) => handleFilterChange('user_id', e.target.value)}
                  data-testid="filter-user"
                >
                  <option value="">All Users</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name || u.username}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">From</label>
                <input
                  type="date"
                  className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                  data-testid="filter-start-date"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">To</label>
                <input
                  type="date"
                  className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                  data-testid="filter-end-date"
                />
              </div>
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="mt-2 text-xs" onClick={clearFilters} data-testid="clear-filters-btn">
                Clear all filters
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No audit entries found</p>
              {hasActiveFilters && <p className="text-xs mt-1">Try adjusting your filters</p>}
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {entries.map(entry => (
                <AuditEntry key={entry.id} entry={entry} userMap={userMap} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between" data-testid="audit-pagination">
          <p className="text-sm text-gray-500">
            Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, total)} of {total}
          </p>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage(p => p - 1)}
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-gray-600">Page {page + 1} of {totalPages}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setPage(p => p + 1)}
              data-testid="next-page-btn"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLog;
